import { useCallback, useEffect, useRef, useState, type FormEvent, type Dispatch, type SetStateAction } from 'react';
import styled from 'styled-components';
import { Button } from '../shared/components/Button';
import { Field, Input, Textarea } from '../shared/components/Field';
import { Alert, EmptyState, Spinner } from '../shared/components/feedback';
import { Switch } from '../shared/components/Switch';
import { PlusIcon, TagIcon } from '../shared/components/icons';
import { useActiveAccount } from '../account/ActiveAccountContext';
import { useDialog } from '../shared/dialog/DialogProvider';
import * as productApi from '../api/product';
import type { Product, ProductImage, ProductUpsertRequest } from '../api/product';
import { ApiError } from '../api/client';
import { YoutubeTopicsPanel } from './products/YoutubeTopicsPanel';

const EMPTY_FORM: ProductUpsertRequest = {
  name: '',
  description: '',
  features: '',
  price: '',
  targetAudience: '',
  pinOwnReplyEnabled: false,
  commentBrief: '',
};

type PanelMode = 'idle' | 'create' | 'detail';
type MaterialTab = 'direct' | 'youtube';

function productImageCount(product: Product) {
  return product.imageCount ?? product.images?.length ?? 0;
}

export default function ProductsPage() {
  const { accounts, selectedAccountId } = useActiveAccount();
  const { alert, confirm } = useDialog();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>('idle');
  const [form, setForm] = useState<ProductUpsertRequest>(EMPTY_FORM);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  );
  const [detailImages, setDetailImages] = useState<ProductImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [assignedAccountIds, setAssignedAccountIds] = useState<number[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSavingId, setAssignmentSavingId] = useState<number | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [crawlKeyword, setCrawlKeyword] = useState('');
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [crawlImporting, setCrawlImporting] = useState(false);
  const [crawlHint, setCrawlHint] = useState<string | null>(null);
  const [crawlCandidates, setCrawlCandidates] = useState<
    productApi.ProductCrawlCandidate[]
  >([]);
  const [crawlSelected, setCrawlSelected] = useState<Set<string>>(new Set());
  const [showCrawl, setShowCrawl] = useState(true);
  const [crawlSearched, setCrawlSearched] = useState(false);
  const [crawlAccountId, setCrawlAccountId] = useState<number | ''>('');
  const [materialTab, setMaterialTab] = useState<MaterialTab>('direct');
  const [search, setSearch] = useState('');
  const detailPaneRef = useRef<HTMLElement>(null);
  const listPaneRef = useRef<HTMLElement>(null);
  const previousPanelMode = useRef<PanelMode>('idle');

  useEffect(() => {
    const previousMode = previousPanelMode.current;
    previousPanelMode.current = panelMode;
    if (!window.matchMedia?.('(max-width: 900px)').matches) return;
    if (panelMode === 'idle' && previousMode === 'idle') return;
    const pane = panelMode === 'idle' ? listPaneRef.current : detailPaneRef.current;
    pane?.scrollIntoView?.({ block: 'start' });
    pane?.focus({ preventScroll: true });
  }, [panelMode, selectedProductId]);

  const loadAssignments = useCallback(async (productId: number) => {
    setAssignmentLoading(true);
    try {
      const data = await productApi.listProductAssignments(productId);
      setAssignedAccountIds(data.threadAccountIds ?? []);
    } catch {
      setAssignedAccountIds([]);
    } finally {
      setAssignmentLoading(false);
    }
  }, []);

  const loadDetailImages = useCallback(async (productId: number) => {
    setImagesLoading(true);
    try {
      const images = await productApi.listProductImages(productId);
      setDetailImages(images);
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, imageCount: images.length, images: undefined }
            : p
        )
      );
    } catch {
      setDetailImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productApi.listProducts();
      setProducts(data);
      if (
        selectedProductId != null &&
        !data.some((p) => p.id === selectedProductId)
      ) {
        setSelectedProductId(null);
        setAssignedAccountIds([]);
        setDetailImages([]);
        setPanelMode('idle');
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : '소재 목록을 불러오지 못했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedProductId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (accounts.length === 0) {
      setCrawlAccountId('');
      return;
    }
    setCrawlAccountId((prev) => {
      if (prev !== '' && accounts.some((a) => a.id === prev)) {
        return prev;
      }
      if (selectedAccountId != null && accounts.some((a) => a.id === selectedAccountId)) {
        return selectedAccountId;
      }
      return accounts[0].id;
    });
  }, [accounts, selectedAccountId]);

  useEffect(() => {
    if (panelMode !== 'detail' || selectedProductId == null) {
      if (panelMode !== 'detail') {
        setAssignedAccountIds([]);
        setDetailImages([]);
      }
      return;
    }

    let cancelled = false;
    void loadAssignments(selectedProductId);

    setImagesLoading(true);
    void productApi
      .listProductImages(selectedProductId)
      .then((images) => {
        if (cancelled) return;
        setDetailImages(images);
        setProducts((prev) =>
          prev.map((p) =>
            p.id === selectedProductId
              ? { ...p, imageCount: images.length, images: undefined }
              : p
          )
        );
      })
      .catch(() => {
        if (!cancelled) setDetailImages([]);
      })
      .finally(() => {
        if (!cancelled) setImagesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [panelMode, selectedProductId, loadAssignments]);

  const openCreate = () => {
    setPanelMode('create');
    setSelectedProductId(null);
    setAssignedAccountIds([]);
    setDetailImages([]);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleCrawlPreview = async () => {
    if (crawlLoading || !crawlKeyword.trim() || accounts.length === 0) return;
    setCrawlLoading(true);
    setCrawlHint(null);
    setCrawlSearched(true);
    try {
      const data = await productApi.previewProductCrawl({
        keyword: crawlKeyword.trim(),
        threadAccountId: crawlAccountId === '' ? null : crawlAccountId,
        limit: 12,
      });
      setCrawlCandidates(data.candidates ?? []);
      setCrawlSelected(new Set());
      setCrawlHint(
        data.hint
          ?? ((data.candidates ?? []).length === 0
            ? '검색 결과가 없습니다. 상품명·브랜드 키워드로 다시 시도해 보세요.'
            : null)
      );
    } catch (err) {
      setCrawlCandidates([]);
      await alert({
        title: '소재 수집 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '소재 후보를 불러오지 못했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setCrawlLoading(false);
    }
  };

  const toggleCrawlSelect = (url: string) => {
    setCrawlSelected((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const handleCrawlImport = async () => {
    if (crawlSelected.size === 0) return;
    setCrawlImporting(true);
    try {
      const selectedCandidates = crawlCandidates.filter((c) =>
        crawlSelected.has(c.sourceUrl)
      );
      const result = await productApi.importProductCrawl({
        keyword: crawlKeyword.trim(),
        threadAccountId: crawlAccountId === '' ? null : crawlAccountId,
        limit: 12,
        selectedSourceUrls: Array.from(crawlSelected),
        candidates: selectedCandidates,
      });
      await alert({
        title: '소재 등록 완료',
        message: `${result.createdCount}개 등록 · ${result.skippedCount}개 건너뜀`,
        tone: 'success',
        confirmText: '확인',
      });
      setCrawlSelected(new Set());
      await load();
      setCrawlCandidates((prev) =>
        prev.map((c) =>
          crawlSelected.has(c.sourceUrl) ? { ...c, alreadyExists: true } : c
        )
      );
    } catch (err) {
      await alert({
        title: '등록 실패',
        message:
          err instanceof ApiError ? err.message : '소재 등록에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setCrawlImporting(false);
    }
  };

  const openDetail = (product: Product) => {
    setPanelMode('detail');
    setSelectedProductId(product.id);
    setDetailImages([]);
    setForm({
      name: product.name ?? '',
      description: product.description ?? '',
      features: product.features ?? '',
      price: product.price ?? '',
      targetAudience: product.targetAudience ?? '',
      pinOwnReplyEnabled: !!product.pinOwnReplyEnabled,
      commentBrief: product.commentBrief ?? '',
    });
    setError(null);
  };

  const closePanel = () => {
    setPanelMode('idle');
    setSelectedProductId(null);
    setAssignedAccountIds([]);
    setDetailImages([]);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload: ProductUpsertRequest = {
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        features: form.features?.trim() || undefined,
        price: form.price?.trim() || undefined,
        targetAudience: form.targetAudience?.trim() || undefined,
        pinOwnReplyEnabled: !!form.pinOwnReplyEnabled,
        commentBrief: form.commentBrief?.trim() || undefined,
      };
      const isCreate = panelMode === 'create';
      const saved = isCreate
        ? await productApi.createProduct(payload)
        : await productApi.updateProduct(selectedProductId!, payload);
      await load();
      openDetail(saved);
      await alert({
        title: isCreate ? '소재 등록 완료' : '소재 수정 완료',
        message:
          '사진을 여러 장 올리면, 계정 설정의 소재 사진 횟수·글당 장수에 맞춰 아직 안 쓴 사진을 붙입니다.',
        tone: 'success',
        confirmText: '확인',
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product: Product) => {
    const ok = await confirm({
      title: '소재 삭제',
      message: `"${product.name}"을(를) 삭제할까요?`,
      confirmText: '삭제',
      cancelText: '취소',
    });
    if (!ok) return;
    try {
      await productApi.deleteProduct(product.id);
      if (selectedProductId === product.id) closePanel();
      await load();
    } catch (err) {
      await alert({
        title: '삭제 실패',
        message: err instanceof ApiError ? err.message : '삭제에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    }
  };

  const toggleAccountAssignment = async (accountId: number, next: boolean) => {
    if (selectedProductId == null) return;
    const prev = assignedAccountIds;
    setAssignedAccountIds((ids) =>
      next
        ? ids.includes(accountId)
          ? ids
          : [...ids, accountId]
        : ids.filter((id) => id !== accountId)
    );
    setAssignmentSavingId(accountId);
    try {
      const data = next
        ? await productApi.assignProductAccount(selectedProductId, accountId)
        : await productApi.unassignProductAccount(selectedProductId, accountId);
      setAssignedAccountIds(data.threadAccountIds ?? []);
    } catch (err) {
      setAssignedAccountIds(prev);
      await alert({
        title: '배정 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '계정 배정을 저장하지 못했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setAssignmentSavingId(null);
    }
  };

  const assignAllAccounts = async () => {
    if (selectedProductId == null) return;
    for (const account of accounts) {
      if (!assignedAccountIds.includes(account.id)) {
        await toggleAccountAssignment(account.id, true);
      }
    }
  };

  const unassignAllAccounts = async () => {
    if (selectedProductId == null) return;
    for (const accountId of [...assignedAccountIds]) {
      await toggleAccountAssignment(accountId, false);
    }
  };

  const handleUploadImages = async (fileList: FileList | null) => {
    if (!selectedProductId || !fileList?.length) return;
    const files = Array.from(fileList).filter((f) =>
      f.type.startsWith('image/')
    );
    if (!files.length) {
      await alert({
        title: '이미지 필요',
        message: '이미지 파일만 업로드할 수 있습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
      return;
    }
    setUploading(true);
    try {
      await productApi.uploadProductImages(selectedProductId, files);
      await Promise.all([load(), loadDetailImages(selectedProductId)]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      await alert({
        title: '업로드 실패',
        message:
          err instanceof ApiError ? err.message : '사진 업로드에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!selectedProductId) return;
    const ok = await confirm({
      title: '사진 삭제',
      message: '이 사진을 소재에서 삭제할까요?',
      confirmText: '삭제',
      cancelText: '취소',
    });
    if (!ok) return;
    try {
      await productApi.deleteProductImage(selectedProductId, imageId);
      await Promise.all([load(), loadDetailImages(selectedProductId)]);
    } catch (err) {
      await alert({
        title: '삭제 실패',
        message: err instanceof ApiError ? err.message : '삭제에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    }
  };

  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const assignedCount = assignedAccountIds.length;
  const searchTerm = search.trim().toLocaleLowerCase();
  const visibleProducts = products.filter((product) =>
    [product.name, product.description, product.targetAudience].some((value) => value?.toLocaleLowerCase().includes(searchTerm))
  );

  return (
    <Page>
      <Header>
        <div>
          <TitleRow>
            <TagIcon size={22} />
            <Title>소재</Title>
          </TitleRow>
          <Sub>
            {materialTab === 'youtube'
              ? '키워드로 유튜브 영상을 찾아 자막을 계정 말투로 바꿔 올립니다.'
              : '글에 쓸 상품·캠페인을 등록하고 계정에 배정하세요.'}
          </Sub>
        </div>
        {materialTab === 'direct' && (
        <HeaderActions>
          <Button
            type="button"
            $variant="ghost"
            aria-expanded={showCrawl}
            aria-controls="material-crawl"
            onClick={() => setShowCrawl((v) => !v)}
          >
            {showCrawl ? '수집 닫기' : '소재 가져오기'}
          </Button>
          <Button type="button" onClick={openCreate}>
            <PlusIcon size={16} />
            소재 추가
          </Button>
        </HeaderActions>
        )}
      </Header>

      <TabBar role="tablist" aria-label="소재 유형" onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 'direct' : event.key === 'End' ? 'youtube' : materialTab === 'direct' ? 'youtube' : 'direct';
        setMaterialTab(next);
        document.getElementById(`material-tab-${next}`)?.focus();
      }}>
        <Tab
          type="button"
          id="material-tab-direct"
          role="tab"
          aria-selected={materialTab === 'direct'}
          aria-controls="material-panel-direct"
          tabIndex={materialTab === 'direct' ? 0 : -1}
          $active={materialTab === 'direct'}
          onClick={() => setMaterialTab('direct')}
        >
          직접 설정한 소재
        </Tab>
        <Tab
          type="button"
          id="material-tab-youtube"
          role="tab"
          aria-selected={materialTab === 'youtube'}
          aria-controls="material-panel-youtube"
          tabIndex={materialTab === 'youtube' ? 0 : -1}
          $active={materialTab === 'youtube'}
          onClick={() => setMaterialTab('youtube')}
        >
          유튜브 소재
        </Tab>
      </TabBar>

      {materialTab === 'youtube' ? (
        <MaterialPanel id="material-panel-youtube" role="tabpanel" aria-labelledby="material-tab-youtube"><YoutubeTopicsPanel /></MaterialPanel>
      ) : (
      <MaterialPanel id="material-panel-direct" role="tabpanel" aria-labelledby="material-tab-direct">

      {error && panelMode === 'idle' && <Alert tone="danger"><RecoveryRow><span>{error}</span><Button type="button" $variant="secondary" $size="sm" onClick={() => void load()}>다시 불러오기</Button></RecoveryRow></Alert>}

      {showCrawl && (
      <CrawlCard id="material-crawl">
        <CrawlTitle>소재 자동 수집</CrawlTitle>
        <CrawlDesc>
          연동된 Threads 계정으로 키워드를 검색합니다. 공개 검색 권한이 없으면
          해당 계정의 글에서 찾습니다.
        </CrawlDesc>
        <CrawlRow>
          <CrawlSelect
            aria-label="소재를 수집할 계정"
            value={crawlAccountId === '' ? '' : String(crawlAccountId)}
            onChange={(e) =>
              setCrawlAccountId(e.target.value ? Number(e.target.value) : '')
            }
            disabled={accounts.length === 0}
          >
            {accounts.length === 0 ? (
              <option value="">연동된 계정 없음</option>
            ) : (
              accounts.map((account) => {
                const name =
                  account.threadAccountProfileResponse?.threadUserName;
                return (
                  <option key={account.id} value={account.id}>
                    {name ? `@${name}` : `계정 #${account.id}`}
                  </option>
                );
              })
            )}
          </CrawlSelect>
          <Input
            aria-label="소재 수집 검색 키워드"
            placeholder="키워드 입력 · 예: 알뜰 쇼핑 팁"
            value={crawlKeyword}
            onChange={(e) => setCrawlKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleCrawlPreview();
              }
            }}
          />
          <Button
            type="button"
            $variant="ghost"
            disabled={
              crawlLoading || !crawlKeyword.trim() || accounts.length === 0
            }
            onClick={() => void handleCrawlPreview()}
          >
            {crawlLoading ? <><Spinner $size={16} /> 검색 중</> : '후보 검색'}
          </Button>
          <Button
            type="button"
            disabled={crawlImporting || crawlSelected.size === 0}
            onClick={() => void handleCrawlImport()}
          >
            {crawlImporting
              ? '등록 중…'
              : `선택 ${crawlSelected.size}개 등록`}
          </Button>
        </CrawlRow>
        {accounts.length === 0 && <HintText>계정 관리에서 Threads 계정을 연동하면 소재를 검색할 수 있습니다. 직접 소재를 추가할 수도 있어요.</HintText>}
        {crawlHint && <HintText role="status">{crawlHint}</HintText>}
        {crawlSearched && !crawlLoading && crawlCandidates.length === 0 && !crawlHint && (
          <HintText>
            검색 결과가 없습니다. 상품명·브랜드 키워드로 다시 시도해 보세요.
          </HintText>
        )}
        {crawlCandidates.length > 0 && (
          <CrawlList>
            {crawlCandidates.map((c) => (
              <CrawlItem key={c.sourceUrl}>
                <input
                  type="checkbox"
                  aria-label={`${c.title || c.sourceUrl} 선택`}
                  disabled={c.alreadyExists}
                  checked={crawlSelected.has(c.sourceUrl)}
                  onChange={() => toggleCrawlSelect(c.sourceUrl)}
                />
                <div>
                  <CrawlItemTitle>
                    [{c.source}] {c.title || c.sourceUrl}
                    {c.alreadyExists ? ' · 이미 등록됨' : ''}
                  </CrawlItemTitle>
                  <CrawlItemMeta>
                    {c.username ? `@${c.username} · ` : ''}
                    {c.snippet}
                  </CrawlItemMeta>
                </div>
              </CrawlItem>
            ))}
          </CrawlList>
        )}
      </CrawlCard>
      )}

      <Board>
        <ListPane ref={listPaneRef} tabIndex={-1} aria-label="소재 목록" $detailOpen={panelMode !== 'idle'}>
          <ListHeader>
            <ListTitle>전체 소재</ListTitle>
            <ListCount>{searchTerm ? `${visibleProducts.length} / ${products.length}` : products.length}</ListCount>
          </ListHeader>
          <SearchTools><Input type="search" aria-label="소재 검색" placeholder="이름, 설명, 대상 검색" value={search} onChange={(event) => setSearch(event.target.value)} /></SearchTools>
          {loading ? (
            <Center>
              <Spinner $size={22} />
            </Center>
          ) : products.length === 0 ? (
            <EmptyState
              title="등록된 소재가 없습니다"
              description="상품이나 캠페인을 등록하고 발행할 계정에 배정하세요."
              action={<Button type="button" onClick={openCreate}><PlusIcon size={16} /> 첫 소재 추가</Button>}
            />
          ) : visibleProducts.length === 0 ? (
            <EmptyState title="검색 결과가 없습니다" description="다른 이름이나 키워드로 검색해 보세요." action={<Button type="button" $variant="secondary" onClick={() => setSearch('')}>검색 초기화</Button>} />
          ) : (
            <ProductList>
              {visibleProducts.map((product) => {
                const meta = [
                  product.price || null,
                  productImageCount(product) > 0
                    ? `사진 ${productImageCount(product)}장`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <ProductItem
                    key={product.id}
                    $active={
                      panelMode === 'detail' && selectedProductId === product.id
                    }
                    type="button"
                    aria-pressed={panelMode === 'detail' && selectedProductId === product.id}
                    onClick={() => openDetail(product)}
                  >
                    <ProductName>{product.name}</ProductName>
                    <ProductMeta>{meta || '사진 없음'}</ProductMeta>
                    {product.targetAudience && (
                      <ProductAudience>{product.targetAudience}</ProductAudience>
                    )}
                  </ProductItem>
                );
              })}
            </ProductList>
          )}
        </ListPane>

        <DetailPane $idle={panelMode === 'idle'} ref={detailPaneRef} tabIndex={-1} aria-label={panelMode === 'create' ? '새 소재 등록' : '소재 상세'}>
          {panelMode === 'idle' && (
            <IdleState>
              <EmptyState
                title="소재를 선택하세요"
                description="목록에서 소재를 선택하면 정보, 사진, 배정 계정을 관리할 수 있습니다."
                action={<Button type="button" $variant="secondary" onClick={openCreate}><PlusIcon size={16} /> 소재 추가</Button>}
              />
            </IdleState>
          )}

          {panelMode === 'create' && (
            <DetailScroll>
              <DetailHeader>
                <CardTitle>소재 등록</CardTitle>
                <Button type="button" $variant="ghost" $size="sm" onClick={closePanel}>
                  목록으로
                </Button>
              </DetailHeader>
              {error && <Alert tone="danger">{error}</Alert>}
              <ProductForm
                form={form}
                setForm={setForm}
                saving={saving}
                submitLabel="등록"
                onSubmit={handleSave}
                onCancel={closePanel}
              />
            </DetailScroll>
          )}

          {panelMode === 'detail' && selectedProduct && (
            <DetailScroll>
              <DetailHeader>
                <div>
                  <CardTitle>{selectedProduct.name}</CardTitle>
                  <DetailSub>
                    {[
                      selectedProduct.price || null,
                      imagesLoading
                        ? '사진 불러오는 중'
                        : detailImages.length > 0
                          ? `사진 ${detailImages.length}장`
                          : productImageCount(selectedProduct) > 0
                            ? `사진 ${productImageCount(selectedProduct)}장`
                            : '사진 없음',
                      assignmentLoading
                        ? '배정 불러오는 중'
                        : `배정 ${assignedCount}개`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </DetailSub>
                </div>
                <DetailHeaderActions>
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    onClick={() => void handleDelete(selectedProduct)}
                  >
                    삭제
                  </Button>
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    onClick={closePanel}
                  >
                    목록으로
                  </Button>
                </DetailHeaderActions>
              </DetailHeader>

              {error && <Alert tone="danger">{error}</Alert>}

              <Section>
                <SectionTitle>소재 정보</SectionTitle>
                <ProductForm
                  form={form}
                  setForm={setForm}
                  saving={saving}
                  submitLabel="수정 저장"
                  onSubmit={handleSave}
                />
              </Section>

              <Section>
                <SectionTitle>사진</SectionTitle>
                <Hint>
                  여러 장을 올리면 계정 설정의 소재 사진 횟수와 글당 장수에
                  맞춰 아직 쓰지 않은 사진을 붙입니다.
                </Hint>
                <Actions>
                  <Button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? <Spinner /> : '사진 추가'}
                  </Button>
                  <HiddenFile
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => void handleUploadImages(e.target.files)}
                  />
                </Actions>
                {imagesLoading ? (
                  <Center>
                    <Spinner $size={22} />
                  </Center>
                ) : detailImages.length === 0 ? (
                  <EmptyState
                    title="등록된 사진이 없습니다"
                    description="여러 장을 올려 두면 계정별로 중복 없이 랜덤 배정됩니다."
                  />
                ) : (
                  <ImageGrid>
                    {detailImages.map((image, index) => (
                      <ImageThumb key={image.id}>
                        <img
                          src={image.publicUrl}
                          alt={`${selectedProduct.name} 사진 ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                        />
                        <Button
                          type="button"
                          $variant="ghost"
                          $size="sm"
                          aria-label={`${selectedProduct.name} 사진 ${index + 1} 삭제`}
                          onClick={() => void handleDeleteImage(image.id)}
                        >
                          삭제
                        </Button>
                      </ImageThumb>
                    ))}
                  </ImageGrid>
                )}
              </Section>

              <Section>
                <SectionTitle>예약 계정</SectionTitle>
                <Hint>
                  이 소재를 쓸 계정을 켜 두세요. 배정은 저장되며, 자동
                  발행(주제 없을 때)과 「예약」에 사용됩니다.
                </Hint>
                {accounts.length === 0 ? (
                  <EmptyState
                    title="연동된 계정이 없습니다"
                    description="계정 관리에서 Threads 계정을 연동하세요."
                  />
                ) : assignmentLoading ? (
                  <Center>
                    <Spinner $size={22} />
                  </Center>
                ) : (
                  <>
                    <AccountActions>
                      <Button
                        type="button"
                        $variant="ghost"
                        $size="sm"
                        disabled={assignmentSavingId != null || assignedAccountIds.length === accounts.length}
                        onClick={() => void assignAllAccounts()}
                      >
                        전체 배정
                      </Button>
                      <Button
                        type="button"
                        $variant="ghost"
                        $size="sm"
                        disabled={assignmentSavingId != null || assignedAccountIds.length === 0}
                        onClick={() => void unassignAllAccounts()}
                      >
                        전체 해제
                      </Button>
                    </AccountActions>
                    <AccountList>
                      {accounts.map((account) => {
                        const profile = account.threadAccountProfileResponse;
                        const label = profile?.threadUserName
                          ? `@${profile.threadUserName}`
                          : `계정 #${account.id}`;
                        const checked = assignedAccountIds.includes(account.id);
                        return (
                          <AccountRow key={account.id}>
                            <AccountSelect>
                              <div>
                                <AccountLabel>{label}</AccountLabel>
                                {profile?.description && (
                                  <AccountTopic>
                                    {profile.description}
                                  </AccountTopic>
                                )}
                              </div>
                              <Switch
                                checked={checked}
                                disabled={assignmentSavingId != null}
                                aria-label={`${label} 배정`}
                                onChange={(next) =>
                                  void toggleAccountAssignment(account.id, next)
                                }
                              />
                            </AccountSelect>
                          </AccountRow>
                        );
                      })}
                    </AccountList>
                    <HintText>
                      배정만 해 두면 자동 발행이 소재를 사용합니다. 개별 글은
                      보드에서 「새 글」로 만드세요.
                    </HintText>
                  </>
                )}
              </Section>
            </DetailScroll>
          )}
        </DetailPane>
      </Board>
      </MaterialPanel>
      )}
    </Page>
  );
}

function ProductForm({
  form,
  setForm,
  saving,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  form: ProductUpsertRequest;
  setForm: Dispatch<SetStateAction<ProductUpsertRequest>>;
  saving: boolean;
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
  onCancel?: () => void;
}) {
  return (
    <Form onSubmit={onSubmit}>
      <Field label="이름" htmlFor="productName" help="상품·캠페인 제목">
        <Input
          id="productName"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="예: 폐점 세일 꿀팁"
          required
        />
      </Field>
      <Field label="설명" htmlFor="productDesc" hint="선택">
        <Textarea
          id="productDesc"
          rows={3}
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
          placeholder="무엇을 다루는 소재인지 짧게"
        />
      </Field>
      <Field
        label="핵심 포인트"
        htmlFor="productFeatures"
        hint="선택"
        help="줄바꿈으로 나눠 적기"
      >
        <Textarea
          id="productFeatures"
          rows={3}
          value={form.features}
          onChange={(e) =>
            setForm((f) => ({ ...f, features: e.target.value }))
          }
          placeholder={'예:\n재고 소진 임박\n평일 오전이 한산함'}
        />
      </Field>
      <FormColumns>
        <Field label="가격" htmlFor="productPrice" hint="선택">
          <Input id="productPrice" value={form.price ?? ''} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="예: 29,900원" />
        </Field>
        <Field label="대상 고객" htmlFor="productAudience" hint="선택">
          <Input id="productAudience" value={form.targetAudience ?? ''} onChange={(e) => setForm((f) => ({ ...f, targetAudience: e.target.value }))} placeholder="예: 자취하는 직장인" />
        </Field>
      </FormColumns>
      <ToggleRow>
        <div>
          <ToggleLabel>발행 후 자동 댓글</ToggleLabel>
          <ToggleHelp>
            이 소재로 글이 나가면 첫 댓글과 팔로업 댓글을 예약합니다. Threads
            공식 API에 댓글 고정 기능이 없어 앱의 고정 배지는 달리지 않습니다.
            계정에 댓글 페르소나가 있어야 동작합니다.
          </ToggleHelp>
        </div>
        <Switch
          checked={!!form.pinOwnReplyEnabled}
          aria-label="발행 후 자동 댓글"
          onChange={(next) =>
            setForm((f) => ({ ...f, pinOwnReplyEnabled: next }))
          }
        />
      </ToggleRow>
      {form.pinOwnReplyEnabled && (
        <Field
          label="팔로업 댓글에 넣을 내용"
          htmlFor="productCommentBrief"
          hint="선택"
          help="상품 링크, 추가 썰, 쿠폰 안내 등. 비워도 소재에 맞게 이어서 씁니다."
        >
          <Textarea
            id="productCommentBrief"
            rows={3}
            value={form.commentBrief ?? ''}
            onChange={(e) =>
              setForm((f) => ({ ...f, commentBrief: e.target.value }))
            }
            placeholder={'예:\nhttps://example.com/product\n재고 얼마 없다는 말도 덧붙여 줘'}
            maxLength={2000}
          />
        </Field>
      )}
      <Actions>
        <Button type="submit" disabled={saving || !form.name.trim()}>
          {saving ? <Spinner /> : submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" $variant="ghost" onClick={onCancel}>
            취소
          </Button>
        )}
      </Actions>
    </Form>
  );
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-height: 0;
`;

const MaterialPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;
const RecoveryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;
const SearchTools = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  input { background: ${({ theme }) => theme.colors.surface}; }
`;
const FormColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const TabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  width: fit-content;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Tab = styled.button<{ $active?: boolean }>`
  border: none;
  cursor: pointer;
  min-height: 44px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text : theme.colors.textSecondary};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.surface : 'transparent'};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 2px rgba(0, 0, 0, 0.06)' : 'none'};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h1`
  font-size: clamp(25px, 2.6vw, 32px);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;
`;

const Sub = styled.p`
  margin-top: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  max-width: 60ch;
`;

const CrawlCard = styled.div`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CrawlTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
`;

const CrawlDesc = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.45;
`;

const CrawlRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  ${Input} {
    flex: 1;
    min-width: 180px;
  }
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    select, ${Input} { grid-column: 1 / -1; min-width: 0; max-width: none; width: 100%; }
    button { width: 100%; }
  }
`;

const CrawlSelect = styled.select`
  min-width: 140px;
  max-width: 220px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  padding: 11px 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CrawlList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow: auto;
`;

const CrawlItem = styled.label`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  cursor: pointer;
  font-size: 13px;
  min-height: 48px;
  input { width: 18px; height: 18px; flex-shrink: 0; margin-top: 2px; accent-color: ${({ theme }) => theme.colors.primary}; }
  > div { min-width: 0; overflow-wrap: anywhere; }
`;

const CrawlItemTitle = styled.div`
  font-weight: 600;
  line-height: 1.35;
`;

const CrawlItemMeta = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HintText = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const Board = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: 0;
  min-height: min(70vh, 720px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

const ListPane = styled.aside<{ $detailOpen: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    display: ${({ $detailOpen }) => $detailOpen ? 'none' : 'flex'};
    max-height: 460px;
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ListTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
`;

const ListCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 2px 8px;
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`;

const ProductItem = styled.button<{ $active?: boolean }>`
  text-align: left;
  padding: 17px 16px;
  min-height: 82px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.surface : 'transparent'};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const ProductName = styled.p`
  margin: 0;
  overflow-wrap: anywhere;
  line-height: 1.5;
  font-size: 14px;
  font-weight: 700;
`;

const ProductMeta = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProductAudience = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DetailPane = styled.section<{ $idle: boolean }>`
  display: flex;
  @media (max-width: 900px) {
    display: ${({ $idle }) => $idle ? 'none' : 'flex'};
  }
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: ${({ theme }) => theme.colors.surface};
  scroll-margin-top: 88px;
`;

const IdleState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const DetailScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  @media (max-width: 600px) { padding: 20px 16px; }
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const DetailHeaderActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const DetailSub = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CardTitle = styled.h2`
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 700;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  min-width: 0;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const ToggleHelp = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.7;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.45;
`;

const AccountActions = styled.div`
  display: flex;
  gap: 6px;
`;

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
`;

const AccountRow = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const AccountSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AccountLabel = styled.span`
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 600;
`;

const AccountTopic = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.4;
`;

const HiddenFile = styled.input`
  display: none;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  content-visibility: auto;
  contain-intrinsic-size: auto 400px;
`;

const ImageThumb = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  content-visibility: auto;
  contain-intrinsic-size: 140px;

  img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.radius.sm};
    background: ${({ theme }) => theme.colors.surface};
  }
`;
